// @noflow
import initI18n from '@/packs/localisation'
import type { Language } from '@/packs/localisation'
import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import React from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Markup } from 'react-render-markup'

import { DiscountBasis } from '@/utils/butternutbox/discountCodes'
import { initGoogleClickIdentifierSession } from '@/utils/googleClickIdentifierSession'
import { isDevelopment } from '@/utils/isDevelopment'
import { playVideosOnScroll } from '@/utils/playVideosOnScroll'
import * as Sentry from '@/utils/sentry'

import useWindowSize from '@/hooks/useWindowSize'

// Components
import { initCookieConsent } from '@/components/cookie_consent'
import BreedSelector from '@/components/shared/elements/BreedSelector'
import { BreedSelectorVariant } from '@/components/shared/elements/BreedSelector/BreedSelector'
import DogProfileSection from '@/components/shared/elements/DogProfileSection/DogProfileSection'
import { initFooterLeadCapture } from '@/components/shared/elements/FooterLeadCapture/FooterLeadCapture'
import FullSectionRoughCostCalculator from '@/components/shared/elements/RoughCostCalculator/FullSectionRoughCostCalculator'
import WonkyTitleText from '@/components/shared/elements/WonkyTitleText/WonkyTitleText'
import initGeoIpWidget from '@/components/shared/geo_ip_widget/initGeoIpWidget'
import { BREAKPOINTS } from '@/components/templates/Base'

import type { Code as CountryCode } from '@/shared_types/rails_models/shipping_countries'

const dataContainer = document.querySelector('#home-content-tag') as HTMLElement
if (!dataContainer)
  Sentry.captureException('Could not find #home-content-tag', {
    tags: {
      product: Sentry.Product.HomePage
    }
  })

const { dataset } = dataContainer
const {
  language,
  isPrewizardGuest,
  discountBasis,
  discountFirstPartValue,
  shippingCountryCode,
  pendingSubscriptionData,
  dogsData,
  shouldSeeReturningUserExperience,
  shouldSeeReturningGuestsTitle,
  ukOrIrelandUser,
  isMobile
} = dataset

const preferredLanguage = (language as Language) || 'en'
const amplitudeApiKey = process.env.AMPLITUDE_API_KEY
const sessionReplayTracking = sessionReplayPlugin({
  debugMode: isDevelopment(),
  sampleRate: 0.2
})
amplitude.add(sessionReplayTracking)

const initLanguage = (): void => {
  initI18n(preferredLanguage)
}

const initBreedSelector = (): void => {
  const breedSelectorElement =
    document.getElementsByClassName('breed-selector')[0]

  if (!breedSelectorElement) return

  const ctaDestination =
    isPrewizardGuest === 'true' ? '/wizard/new' : '/plans/recipes'

  const BreedSelectorComponent = () => {
    const { windowWidth } = useWindowSize()

    return (
      <BreedSelector
        isPreWizardGuest={isPrewizardGuest === 'true'}
        ctaDestination={ctaDestination}
        variant={BreedSelectorVariant.Homepage}
        scrollToOnFocus={
          windowWidth <= BREAKPOINTS.lg ? { topOffset: 60 } : false
        }
        screenIdentifier="homepage"
      />
    )
  }

  ReactDOM.render(<BreedSelectorComponent />, breedSelectorElement)
}

const initWonkyTitleText = (elementClassName: string, titleText: string) => {
  const wonkyTextElements = document.getElementsByClassName(elementClassName)

  const wonkyTextElementArray = [...wonkyTextElements]

  if (!wonkyTextElements) return null

  wonkyTextElementArray.forEach((element) => {
    ReactDOM.render(
      <WonkyTitleText namespace="home_page" text={titleText} />,
      element
    )
  })
}

const initDogProfileSection = () => {
  if (shouldSeeReturningUserExperience !== 'true') return null

  const dogProfileSectionElement = document.getElementById(
    'dog-profile-section'
  ) as HTMLElement

  if (!dogProfileSectionElement) return null

  // Parse the pending subscription data from JSON
  let parsedPendingSubscriptionData = null
  if (pendingSubscriptionData) {
    try {
      parsedPendingSubscriptionData = JSON.parse(pendingSubscriptionData)
    } catch (error) {
      Sentry.captureException('Failed to parse pending subscription data', {
        tags: {
          product: Sentry.Product.HomePage
        }
      })

      return null
    }
  }

  // Parse the dogs data from JSON
  let parsedDogsData = null
  if (dogsData) {
    try {
      parsedDogsData = JSON.parse(dogsData)
    } catch (error) {
      Sentry.captureException('Failed to parse dogs data', {
        tags: {
          product: Sentry.Product.HomePage
        }
      })

      return null
    }
  }

  ReactDOM.render(
    <DogProfileSection
      pendingSubscriptionData={parsedPendingSubscriptionData}
      dogsData={parsedDogsData}
    />,
    dogProfileSectionElement
  )
}

const initRoughCostCalculator = () => {
  const roughCostCalculatorElement = document.getElementById(
    'rough-cost-calculator'
  ) as HTMLElement

  // We return null for users who aren't shown the calculator
  if (!roughCostCalculatorElement) return null

  ReactDOM.render(
    <FullSectionRoughCostCalculator
      preferredLanguage={preferredLanguage}
      screenIdentifier="homepage"
      discountBasis={discountBasis as DiscountBasis}
      discountValue={parseInt(discountFirstPartValue || '') || 0}
      shippingCountryCode={shippingCountryCode as CountryCode}
      showImage
    />,
    roughCostCalculatorElement
  )
}

const draggableCarousel = (elementId: string) => {
  const element = document.getElementById(elementId)
  if (!element) return

  let pos = { top: 0, left: 0, x: 0, y: 0 }

  const mouseMoveHandler = function (e: MouseEvent) {
    element.scrollLeft = pos.left - (e.clientX - pos.x)
  }

  const mouseUpHandler = () => {
    document.removeEventListener('mousemove', mouseMoveHandler)
    document.removeEventListener('mouseup', mouseUpHandler)

    element.style.cursor = 'grab'
    element.style.removeProperty('user-select')
  }

  const mouseDownHandler = (e: MouseEvent) => {
    element.style.cursor = 'grabbing'
    element.style.userSelect = 'none'

    pos = {
      left: element.scrollLeft,
      top: element.scrollTop,
      x: e.clientX,
      y: e.clientY
    }

    document.addEventListener('mousemove', mouseMoveHandler)
    document.addEventListener('mouseup', mouseUpHandler)
  }

  element.addEventListener('mousedown', mouseDownHandler)
}
const wizardLocalStorageItem = localStorage.getItem('wizardState')
const wizardState = wizardLocalStorageItem
  ? JSON.parse(wizardLocalStorageItem)
  : { dogs: [] }
const { dogs } = wizardState

const DynamicTitle = ({
  displayUkOrIrelandUser
}: {
  displayUkOrIrelandUser: boolean
}) => {
  const { t } = useTranslation()

  const count = dogs.length
  const dogName = count === 1 ? dogs[0].name : null

  if (displayUkOrIrelandUser) {
    return (
      <Markup
        allowed={['br']}
        markup={t('homepage:hero.title_summer_campaign_2025_html')}
      />
    )
  }
  return (
    <span>
      <Markup
        allowed={['br']}
        markup={
          count > 1
            ? t('home_page:hero_section.title_returning_guest_dynamic_plural')
            : count === 1 && dogName.length > 12
            ? t('home_page:hero_section.title_returning_guest_dynamic_singular')
            : t('home_page:hero_section.title_returning_guest_dynamic', {
                dogName
              })
        }
      />
    </span>
  )
}

const initDynamicTitle = (
  elementClassName: string,
  displayUkOrIrelandUser: boolean
) => {
  const dynamicTitleElements = document.getElementsByClassName(elementClassName)

  const dynamicTitleElementsArray = [...dynamicTitleElements]

  if (!dynamicTitleElements) return null

  Array.from(dynamicTitleElementsArray).forEach((element) => {
    ReactDOM.render(
      <DynamicTitle displayUkOrIrelandUser={displayUkOrIrelandUser} />,
      element
    )
  })
}

document.addEventListener('DOMContentLoaded', (): void => {
  const className = isMobile === 'true' ? 'title-text-mobile' : 'title-text'
  const title =
    isMobile === 'true' ? 'hero_section.title_mobile' : 'hero_section.title'

  initLanguage()
  if (
    shouldSeeReturningGuestsTitle === 'true' &&
    dogs.length > 0 &&
    dogs[0].name.length > 0
  ) {
    initDynamicTitle(className, false)
  } else if (ukOrIrelandUser === 'true') {
    initDynamicTitle(className, true)
  } else {
    initWonkyTitleText(className, title)
  }

  initWonkyTitleText(
    'wonky-title-text-returning-guest',
    'hero_section.title_returning_guest'
  )
  initDogProfileSection()
  initRoughCostCalculator()
  initGoogleClickIdentifierSession()
  initGeoIpWidget({
    desktopNav: true,
    mobileSidebar: true,
    contentTagElementId: 'home-content-tag'
  })
  playVideosOnScroll()
  initBreedSelector()
  initCookieConsent()
  initFooterLeadCapture()
  draggableCarousel('ingredient-carousel')

  !isDevelopment() && amplitudeApiKey && amplitude.init(amplitudeApiKey)
})
